import { Card } from '../../components/ui/Card';
import type { ResearchBrief } from '../../api/content';
import { formatDayMonthYearTime } from '../../utils/dateFormat';

interface ResearchPreviewProps {
  brief: ResearchBrief;
}

export function ResearchPreview({ brief }: ResearchPreviewProps) {
  return (
    <Card className="p-4 space-y-4">
      <h3 className="text-body-md font-medium text-text-primary">Research Brief</h3>
      <p className="text-body-xs text-text-tertiary">
        Generated {formatDayMonthYearTime(brief.generatedAt)} | Language: {brief.language.toUpperCase()}
      </p>

      {brief.topicOverview.findings.length > 0 && (
        <div>
          <h4 className="text-body-sm font-medium text-text-secondary mb-1">Key Findings</h4>
          <ul className="space-y-1">
            {brief.topicOverview.findings.slice(0, 5).map((finding, i) => (
              <li key={i} className="text-body-xs text-text-secondary pl-3 border-l-2 border-border">
                {finding}
              </li>
            ))}
          </ul>
        </div>
      )}

      {brief.topicOverview.statistics.length > 0 && (
        <div>
          <h4 className="text-body-sm font-medium text-text-secondary mb-1">Statistics</h4>
          <ul className="space-y-1">
            {brief.topicOverview.statistics.slice(0, 6).map((stat, i) => (
              <li key={i} className="text-body-xs text-text-secondary">
                {stat.value}
                {stat.source !== 'Perplexity Research' && (
                  <span className="text-text-tertiary ml-1">({stat.source})</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {brief.topicOverview.sources.length > 0 && (
        <div>
          <h4 className="text-body-sm font-medium text-text-secondary mb-1">Sources</h4>
          <ul className="space-y-1">
            {brief.topicOverview.sources.slice(0, 5).map((source, i) => (
              <li key={i} className="text-body-xs">
                <span className="text-text-secondary">{source.title}</span>
                {source.url && (
                  <span className="text-text-tertiary ml-1 break-all">({source.url})</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {brief.competitorLandscape && brief.competitorLandscape.findings.length > 0 && (
        <div>
          <h4 className="text-body-sm font-medium text-text-secondary mb-1">Competitor Landscape</h4>
          <ul className="space-y-1">
            {brief.competitorLandscape.findings.slice(0, 3).map((f, i) => (
              <li key={i} className="text-body-xs text-text-secondary pl-3 border-l-2 border-border">
                {f}
              </li>
            ))}
          </ul>
        </div>
      )}

      {brief.icpContext && brief.icpContext.findings.length > 0 && (
        <div>
          <h4 className="text-body-sm font-medium text-text-secondary mb-1">Industry Context</h4>
          <ul className="space-y-1">
            {brief.icpContext.findings.slice(0, 3).map((f, i) => (
              <li key={i} className="text-body-xs text-text-secondary pl-3 border-l-2 border-border">
                {f}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
